/**
 * LEADS-LIST-SALESFORCE-VIEWS-V1 -- the "Recently viewed" client memory for
 * the canonical Leads list (`/dashboard/leads`).
 *
 * A VIEW-LAYER MEMORY, never a server read. It records the ids of leads the
 * caller lawfully OPENED from the list (quick view or the full-record door)
 * and returns them most-recent-first, so the Salesforce "Recently viewed"
 * view can narrow the CURRENT fenced page to those rows, ordered by open
 * time. It is NOT an access decision and cannot widen one:
 *
 *   - PERSISTENCE: per-tab sessionStorage -- the SAME storage class the
 *     global clinic context and the walk-in session already use
 *     (frontend/src/dashboard/clinic-context.tsx idiom). Per-tab (not
 *     localStorage) honors the shared-clinic-workstation leak concern the
 *     list's persistence rung already cites (no browser-local state that
 *     outlives the tab on a shared machine).
 *   - NO PHI: the stored value is a lead ID + an open timestamp ONLY (the
 *     clinic-context "id only, never PHI" rule). No name, phone, or clinical
 *     field ever enters storage.
 *   - FENCE-SAFE BY CONSTRUCTION: the ids are only ever RENDERED by
 *     intersecting them with the records the server already fenced onto the
 *     current page (`orderRecordsByRecency`). A crafted or stale id -- even a
 *     foreign lead id injected into storage -- can never surface, because it
 *     is absent from the fenced page the intersection runs against. An
 *     ownership-fenced agent's Recently-viewed therefore can never contain a
 *     foreign lead.
 *
 * PURE by default: the ordering + push logic are pure functions (exported
 * for deterministic tests -- callers inject the clock); only the two
 * storage wrappers touch sessionStorage, each try/catch-guarded so privacy
 * mode degrades to an empty memory, never a throw.
 */

/** Per-tab storage key (versioned; the clinic-context precedent). */
export const RECENTLY_VIEWED_STORAGE_KEY = 'adoracare.leads.recently-viewed.v1';

/** How many opened leads the memory retains (most-recent-first). */
export const RECENTLY_VIEWED_CAP = 30;

/** One recorded open: a lawful lead id + the ms epoch it was opened. */
export interface RecentlyViewedEntry {
  readonly id: string;
  readonly at: number;
}

/** A controlled-shape lead id (digits/tokens only; never PHI, bounded). An
 *  id that fails this shape is dropped -- storage carries only clean tokens. */
const LEAD_ID_SHAPE = /^[A-Za-z0-9_-]{1,40}$/;

function isValidId(id: unknown): id is string {
  return typeof id === 'string' && LEAD_ID_SHAPE.test(id);
}

/**
 * Pure: fold a fresh open into the ordered entry list. The opened id moves
 * to the FRONT (most recent), any prior entry for the same id is removed
 * (dedupe), and the list is capped. An invalid id or non-finite clock
 * returns the list unchanged (fail-safe, never a throw).
 */
export function applyOpen(
  entries: readonly RecentlyViewedEntry[],
  id: string,
  atMs: number,
  cap: number = RECENTLY_VIEWED_CAP,
): readonly RecentlyViewedEntry[] {
  if (!isValidId(id) || !Number.isFinite(atMs)) return entries;
  const withoutId = entries.filter((e) => e.id !== id);
  return [{ id, at: atMs }, ...withoutId].slice(0, Math.max(0, cap));
}

/** Pure: parse a raw storage string into a clean, ordered entry list. Any
 *  malformed shape yields an empty list (fail-closed, never a throw). */
export function parseEntries(raw: string | null): readonly RecentlyViewedEntry[] {
  if (raw === null) return [];
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(value)) return [];
  const out: RecentlyViewedEntry[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (item === null || typeof item !== 'object') continue;
    const rec = item as { id?: unknown; at?: unknown };
    if (!isValidId(rec.id) || typeof rec.at !== 'number' || !Number.isFinite(rec.at)) {
      continue;
    }
    if (seen.has(rec.id)) continue;
    seen.add(rec.id);
    out.push({ id: rec.id, at: rec.at });
    if (out.length >= RECENTLY_VIEWED_CAP) break;
  }
  return out;
}

/**
 * Pure + FENCE-SAFE: narrow a fenced page of records to the ones present in
 * the recency memory, ordered most-recently-opened first. The intersection
 * runs against `records` (the server-fenced page), so only ids the fence
 * ALREADY served can appear -- a stale or crafted recency id is silently
 * dropped. Records not in the memory are omitted. Ties (equal or absent in
 * the id-order map) preserve the incoming record order.
 */
export function orderRecordsByRecency<T extends { readonly safeLeadId: string }>(
  records: readonly T[],
  recentIds: readonly string[],
): T[] {
  const rank = new Map<string, number>();
  recentIds.forEach((id, i) => {
    if (!rank.has(id)) rank.set(id, i);
  });
  return records
    .filter((r) => rank.has(r.safeLeadId))
    .sort((a, b) => (rank.get(a.safeLeadId) ?? 0) - (rank.get(b.safeLeadId) ?? 0));
}

// ---- Storage wrappers (the only impure surface; try/catch-guarded) ---------

/** Read the ordered recently-opened ids (most-recent-first) from per-tab
 *  storage. Storage unavailable / malformed -> [] (never a throw). */
export function readRecentlyViewedIds(): string[] {
  try {
    const raw = sessionStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY);
    return parseEntries(raw).map((e) => e.id);
  } catch {
    return [];
  }
}

/**
 * Record that the caller opened a lead, persisting the updated memory.
 * Returns the fresh ordered id list so a caller can update reactive state
 * without a second read. Storage unavailable -> the open is a no-op and the
 * prior (in-memory) order is returned honestly.
 */
export function recordLeadOpened(id: string, atMs: number): string[] {
  try {
    const current = parseEntries(sessionStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY));
    const next = applyOpen(current, id, atMs);
    sessionStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(next));
    return next.map((e) => e.id);
  } catch {
    return readRecentlyViewedIds();
  }
}
